import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
    {
        key: "digest",
        title: "Weekly digest",
        desc: "A summary of likes, comments, follows, and DMs you missed while notifications were muted.",
    },
    {
        key: "welcome",
        title: "Welcome & onboarding",
        desc: "Tips for setting up your profile and getting the most out of RealWorld Outdoors in your first week.",
    },
    {
        key: "marketing",
        title: "Product & community updates",
        desc: "New features, creator spotlights, community events. Infrequent — no spam, we promise.",
    },
];

export default function EmailSettings({ embedded = false }) {
    const [prefs, setPrefs] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/users/me/email-preferences")
            .then(({ data }) => setPrefs(data))
            .catch((err) => toast.error(formatApiError(err)));
    }, []);

    const toggle = async (key) => {
        const next = { ...prefs, [key]: !prefs[key] };
        setPrefs(next);
        setSaving(true);
        try {
            const { data } = await api.patch("/users/me/email-preferences", { [key]: next[key] });
            setPrefs(data);
        } catch (err) {
            toast.error(formatApiError(err));
            setPrefs((p) => ({ ...p, [key]: !next[key] })); // rollback
        } finally {
            setSaving(false);
        }
    };

    if (!prefs) {
        return (
            <div className={embedded ? "py-8 flex items-center justify-center" : "min-h-screen flex items-center justify-center bg-alpine-mist"}>
                <Loader2 className="w-8 h-8 animate-spin text-ember-orange" />
            </div>
        );
    }

    const cardNode = (
        <div className="bg-snowcap border border-ridge-line rounded-lg p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-ember-orange/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-ember-orange" />
                </div>
                <div>
                    <h1 className="font-display font-black text-2xl text-midnight-pine">
                        Email preferences
                    </h1>
                    <p className="text-xs text-slate-gray">
                        Transactional emails (password reset, account security) are always delivered.
                    </p>
                </div>
            </div>

            <ul className="divide-y divide-ridge-line">
                {CATEGORIES.map((c) => (
                    <li key={c.key} className="py-4 flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-midnight-pine">{c.title}</div>
                            <div className="text-sm text-slate-gray mt-1">{c.desc}</div>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={!!prefs[c.key]}
                            data-testid={`email-toggle-${c.key}`}
                            onClick={() => toggle(c.key)}
                            disabled={saving}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                prefs[c.key] ? "bg-ember-orange" : "bg-ridge-line"
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    prefs[c.key] ? "translate-x-6" : "translate-x-1"
                                }`}
                            />
                        </button>
                    </li>
                ))}
            </ul>

            <p className="mt-6 text-xs text-slate-gray leading-relaxed">
                We respect your inbox. Every non-transactional email also contains a one-click
                unsubscribe link for its category.
            </p>
        </div>
    );

    if (embedded) return cardNode;

    return (
        <div className="min-h-screen bg-alpine-mist py-8 px-4">
            <div className="max-w-2xl mx-auto">
                <Link
                    to="/settings"
                    data-testid="email-settings-back"
                    className="inline-flex items-center gap-2 text-sm font-bold text-granite mb-4 hover:text-midnight-pine"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to settings
                </Link>
                {cardNode}
            </div>
        </div>
    );
}
